// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third-Party Library Imports
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

// Local Component Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import ReportActions from "./components/report-actions";
import ReportDropzone from "./components/report-dropzone";
import ReportFileChip from "./components/report-file-chip";
import ReportPreview from "./components/report-preview";
import ReportSuccess from "./components/report-success";

// Service Imports
import { CREATE_BREADCRUMB } from "../../store/actions";
import TransactionReportIpService, {
  FillResult,
} from "./transaction-report-ip.service";
import {
  isExcelFile,
  parseReport,
  ParsedReport,
} from "./transaction-report.columns";

import "./transaction-report-ip.scss";

/**
 * Operations upload the daily transaction report; the backend fills in
 * `CLIENT_IP` for each row and returns the completed workbook.
 *
 * The sheet's contents are not validated here: the backend decides what a
 * valid report is. Parsing is for display only — a row count and a sample so
 * the operator can confirm they grabbed the right export — and a file that
 * cannot be parsed is still uploaded.
 */
const TransactionReportIp: React.FC = (): ReactElement => {
  const dispatch = useDispatch();
  const service = new TransactionReportIpService(dispatch);

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedReport | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState<FillResult | null>(null);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          { text: `Customers list`, type: "link", link: "/home/customers-list" },
        ],
      },
    });
  }, []);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setCompleted(null);
  };

  const accept = async (selected: File) => {
    if (!isExcelFile(selected)) {
      toast.error(
        "Only Excel or CSV files (.xlsx, .xls, .csv) can be uploaded. Please try again with a valid export."
      );
      return;
    }

    setCompleted(null);
    setFile(selected);

    // Preview only, and never a gate: if the workbook cannot be read — or
    // reads as empty — the upload still goes ahead, because the backend is
    // what decides whether the file is usable. A parse that yields no rows
    // simply shows no preview rather than an empty table.
    try {
      const report = parseReport(await selected.arrayBuffer());
      setParsed(report.rows.length > 0 ? report : null);
    } catch {
      setParsed(null);
    }
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    const result = await service.fillClientIps(file);
    setSubmitting(false);
    // `fileName` is only set when a workbook actually came back, so the
    // success copy never claims a download that did not happen.
    if (result.ok) setCompleted(result);
  };

  return (
    <section className="transaction-ip-container">
      <BreadcrumbComponent />

      <div className="page-title">
        <h3>Online trading transactions — client IPs</h3>
        <p>
          Upload the daily transaction report and the system fills in the client
          IP for each row, then returns the completed workbook.
        </p>
      </div>

      <div className="panel">
        <header>
          <h4>Daily transaction report</h4>
          {parsed && !completed && (
            <span className="count">
              {parsed.rows.length.toLocaleString("en-US")} rows
            </span>
          )}
        </header>

        {completed ? (
          <ReportSuccess fileName={completed.fileName} />
        ) : (
          <div className="body">
            {!file ? (
              <ReportDropzone onFile={(selected) => void accept(selected)} />
            ) : (
              <>
                <ReportFileChip
                  name={file.name}
                  size={file.size}
                  rows={parsed?.rows.length}
                  columns={parsed?.headers.length}
                  disabled={submitting}
                  onRemove={reset}
                />
                {parsed && <ReportPreview parsed={parsed} />}
              </>
            )}
          </div>
        )}

        <ReportActions
          hasFile={Boolean(file)}
          submitting={submitting}
          completed={Boolean(completed)}
          onReset={reset}
          onSubmit={() => void submit()}
        />
      </div>
    </section>
  );
};

export default TransactionReportIp;
